/**
 * Organizzazione › Persone (07/10/2026): the employees of K-Rev, their data,
 * the salary of every month (13 a year: twelve months and the tredicesima),
 * the advances paid before it and the payments. Reserved: the module is given
 * to no profile. Only employees for now, with the TFR paid to the pension
 * funds (1.68); the company cost from the payroll is in payroll.ts (2.37).
 */
import { z } from 'zod';
import { addDays, daysInMonth, isoDate } from './dates.js';
import { fromCents, toCents } from './money.js';
import { ibanError, italianTaxCodeError, normaliseIban } from './tax-ids.js';
import { emailSchema, isoDateSchema, optionalText, requiredText } from './validation.js';
export const EMPLOYMENT_CONTRACTS = ['indeterminato', 'determinato', 'apprendistato'];
export const EMPLOYMENT_CONTRACT_LABELS = {
    indeterminato: 'Tempo indeterminato',
    determinato: 'Tempo determinato',
    apprendistato: 'Apprendistato',
};
/**
 * The relationship of a person with K-Rev (2.38): an employee, with payslips,
 * or an external collaborator with a VAT number, who invoices and whose cost is
 * typed by hand (decision 24/10/2026). Collaborators never enter K-Management.
 */
export const RELATIONSHIPS = ['dipendente', 'collaboratore'];
export const RELATIONSHIP_LABELS = {
    dipendente: 'Dipendente',
    collaboratore: 'Collaboratore con partita IVA',
};
/** Where the TFR goes: kept by the company, or paid to a pension fund. */
export const TFR_DESTINATIONS = ['azienda', 'fondo'];
export const TFR_DESTINATION_LABELS = {
    azienda: 'In azienda',
    fondo: 'Fondo pensione',
};
/** The table of Tabelle with the pension funds (Amundi, Allianz…). */
export const PENSION_FUNDS_TABLE = 'fondi-pensione';
/** The twelve months and the tredicesima, the 13th salary of the year. */
export const SALARY_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
const MONTH_NAMES = [
    'Gennaio',
    'Febbraio',
    'Marzo',
    'Aprile',
    'Maggio',
    'Giugno',
    'Luglio',
    'Agosto',
    'Settembre',
    'Ottobre',
    'Novembre',
    'Dicembre',
    'Tredicesima',
];
export function salaryMonthLabel(month) {
    return MONTH_NAMES[month - 1] ?? String(month);
}
/** «Settembre 2026», «Tredicesima 2026». */
export function salaryPeriodLabel(year, month) {
    return `${salaryMonthLabel(month)} ${year}`;
}
/** The days an employee in force must cover: the month, December for the tredicesima. */
export function salaryPeriodDays(year, month) {
    const calendar = month === 13 ? 12 : month;
    const last = new Date(Date.UTC(year, calendar, 0)).getUTCDate();
    const mm = String(calendar).padStart(2, '0');
    return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}` };
}
/** Whether an employee is in force in a period: hired by its end, not gone before its start. */
export function inForce(employee, from, to) {
    return employee.hiredOn <= to && (employee.leftOn === null || employee.leftOn >= from);
}
const positiveMoney = z
    .string()
    .trim()
    .regex(/^\d{1,9}([.,]\d{1,2})?$/, { message: 'Importo non valido' })
    .transform((value) => fromCents(toCents(value.replace(',', '.'))))
    .refine((value) => toCents(value) > 0, { message: 'L’importo deve essere maggiore di zero' });
export const employeeSchema = z
    .object({
    lastName: requiredText(80),
    firstName: requiredText(80),
    /** Optional only for who works for K-Rev through a company (2.40): the service checks it. */
    taxCode: optionalText(16).transform((value) => value ? value.toUpperCase().replace(/\s+/g, '') : null),
    birthDate: isoDateSchema.nullable(),
    birthPlace: optionalText(80),
    address: optionalText(200),
    phone: optionalText(30),
    personalEmail: emailSchema.nullable(),
    workEmail: emailSchema.nullable(),
    relationship: z.enum(RELATIONSHIPS).default('dipendente'),
    /** The subject of Anagrafica that invoices for a collaborator (2.38). */
    subjectId: z.uuid().nullable().default(null),
    /** Employees only: null for a collaborator. */
    contract: z.enum(EMPLOYMENT_CONTRACTS).nullable(),
    /** Null for full time. */
    partTimePercent: z.number().int().min(1).max(99).nullable(),
    ccnl: optionalText(80),
    level: optionalText(40),
    jobTitle: optionalText(80),
    /** The code of the table «Ruoli aziendali». */
    roleCode: optionalText(30),
    hiredOn: isoDateSchema,
    leftOn: isoDateSchema.nullable(),
    iban: optionalText(40).transform((value) => (value ? normaliseIban(value) : null)),
    /** The code of the account of «Conti» the salary is paid from. */
    payingAccountCode: optionalText(30),
    /** Employees only: null for a collaborator. */
    tfr: z.enum(TFR_DESTINATIONS).nullable(),
    /** The code of the table «Fondi pensione». */
    pensionFundCode: optionalText(30),
    fundJoinedOn: isoDateSchema.nullable(),
    notes: optionalText(500),
})
    .superRefine((body, context) => {
    const taxCodeError = body.taxCode === null
        ? body.relationship === 'dipendente'
            ? 'Indica il codice fiscale'
            : null
        : /^[A-Z0-9]{16}$/.test(body.taxCode)
            ? italianTaxCodeError(body.taxCode)
            : 'Codice fiscale: 16 caratteri';
    if (taxCodeError)
        context.addIssue({ code: 'custom', path: ['taxCode'], message: taxCodeError });
    const error = body.iban ? ibanError(body.iban) : null;
    if (error)
        context.addIssue({ code: 'custom', path: ['iban'], message: error });
    if (body.leftOn && body.leftOn < body.hiredOn) {
        context.addIssue({
            code: 'custom',
            path: ['leftOn'],
            message: 'La cessazione viene dopo l’assunzione',
        });
    }
    if (body.relationship === 'collaboratore') {
        // A collaborator invoices: first the subject of Anagrafica (2.39).
        if (!body.subjectId) {
            context.addIssue({
                code: 'custom',
                path: ['subjectId'],
                message: 'Scegli il fornitore in Anagrafica',
            });
        }
        return;
    }
    if (!body.contract) {
        context.addIssue({ code: 'custom', path: ['contract'], message: 'Indica il contratto' });
    }
    if (!body.tfr) {
        context.addIssue({ code: 'custom', path: ['tfr'], message: 'Indica dove va il TFR' });
    }
    if (body.tfr === 'fondo' && !body.pensionFundCode) {
        context.addIssue({
            code: 'custom',
            path: ['pensionFundCode'],
            message: 'Indica il fondo pensione',
        });
    }
})
    // What belongs to an employee does not stay on a collaborator, nor the supplier on an employee.
    .transform((body) => body.relationship === 'collaboratore'
    ? {
        ...body,
        contract: null,
        partTimePercent: null,
        ccnl: null,
        level: null,
        iban: null,
        payingAccountCode: null,
        tfr: null,
        pensionFundCode: null,
        fundJoinedOn: null,
    }
    : { ...body, subjectId: null });
/**
 * Whether a subject of Anagrafica is a company rather than a person (2.40): a
 * person has the type «persona fisica» (FPF) or a personal tax code, as the
 * import from Gestify decides it.
 */
export function subjectIsCompany(subject) {
    if (subject.typeCode === 'FPF')
        return false;
    return !/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i.test(subject.taxCode ?? '');
}
/** «Gialli Paolo» → last name «Gialli», first name «Paolo»: a proposal, changed by hand. */
export function splitPersonName(name) {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length < 2)
        return { lastName: words[0] ?? '', firstName: '' };
    return { lastName: words[0], firstName: words.slice(1).join(' ') };
}
/** «Rossi Giulia». */
export function employeeName(employee) {
    return `${employee.lastName} ${employee.firstName}`;
}
/** «Tempo indeterminato · tempo pieno», «Apprendistato · part-time 50%», «Collaboratore con partita IVA». */
export function employmentLabel(employee) {
    if (employee.relationship === 'collaboratore' || !employee.contract) {
        return RELATIONSHIP_LABELS.collaboratore;
    }
    return `${EMPLOYMENT_CONTRACT_LABELS[employee.contract]} · ${employee.partTimePercent === null ? 'tempo pieno' : `part-time ${employee.partTimePercent}%`}`;
}
/* --- Salaries ------------------------------------------------------------ */
/** An advance is paid before the salary; the balance pays what is left. */
export const SALARY_PAYMENT_KINDS = ['acconto', 'saldo'];
export const SALARY_PAYMENT_KIND_LABELS = {
    acconto: 'Acconto',
    saldo: 'Saldo',
};
export const salaryNetSchema = z.object({
    /** The net of the payslip; null takes it away. */
    net: positiveMoney.nullable(),
});
export const monthSalariesSchema = z.object({
    items: z.array(z.object({ employeeId: z.uuid(), net: positiveMoney.nullable() })).max(500),
});
export const salaryPaymentSchema = z.object({
    kind: z.enum(SALARY_PAYMENT_KINDS),
    date: isoDateSchema,
    amount: positiveMoney,
    accountCode: optionalText(30),
    note: optionalText(300),
});
/**
 * - vuota: nothing loaded;
 * - acconto: advances paid, the net not loaded yet;
 * - da-pagare: the net loaded, nothing paid;
 * - parziale: paid in part;
 * - pagato: paid in full;
 * - eccedente: paid more than the net.
 */
export const SALARY_STATES = [
    'vuota',
    'acconto',
    'da-pagare',
    'parziale',
    'pagato',
    'eccedente',
];
export const SALARY_STATE_LABELS = {
    vuota: '—',
    acconto: 'Acconto dato',
    'da-pagare': 'Da pagare',
    parziale: 'Pagato in parte',
    pagato: 'Pagato',
    eccedente: 'Pagato più del netto',
};
/** Where the salary of a month stands, from its net and its payments. */
export function salaryFigures(net, payments) {
    const advances = payments
        .filter((payment) => payment.kind === 'acconto')
        .reduce((sum, payment) => sum + toCents(payment.amount), 0);
    const balance = payments
        .filter((payment) => payment.kind === 'saldo')
        .reduce((sum, payment) => sum + toCents(payment.amount), 0);
    const paidOn = payments.reduce((last, payment) => (last === null || payment.date > last ? payment.date : last), null);
    const figures = { advances: fromCents(advances), paid: fromCents(balance), paidOn };
    if (net === null) {
        return { ...figures, net, residual: null, state: advances + balance > 0 ? 'acconto' : 'vuota' };
    }
    const residual = toCents(net) - advances - balance;
    const state = residual < 0
        ? 'eccedente'
        : residual === 0
            ? 'pagato'
            : advances + balance > 0
                ? 'parziale'
                : 'da-pagare';
    return { ...figures, net, residual: fromCents(residual), state };
}
/** The sums of a list of salaries; a month without its net counts for its payments only. */
export function salaryTotals(items) {
    const sum = (pick) => fromCents(items.reduce((total, item) => total + toCents(pick(item) ?? '0'), 0));
    return {
        net: sum((item) => item.net),
        advances: sum((item) => item.advances),
        paid: sum((item) => item.paid),
        residual: sum((item) => item.residual),
    };
}
/* --- TFR paid to the pension funds (1.68) --------------------------------- */
/**
 * The payments of the TFR to the pension fund of an employee (13/10/2026):
 * linked from the bank movement in Tesoreria › Banche, then given the period
 * they cover, from one day to another.
 */
export const tfrCoverageSchema = z
    .object({
    coveredFrom: isoDateSchema,
    coveredTo: isoDateSchema,
})
    .superRefine((body, context) => {
    if (body.coveredTo < body.coveredFrom) {
        context.addIssue({
            code: 'custom',
            path: ['coveredTo'],
            message: 'La fine viene dopo l’inizio',
        });
    }
});
/** Where the TFR of an employee is covered, from the periods of its payments. */
export function tfrCoverage(payments) {
    const periods = payments
        .filter((payment) => payment.coveredFrom !== null && payment.coveredTo !== null)
        .map((payment) => ({ from: payment.coveredFrom, to: payment.coveredTo }))
        .sort((a, b) => a.from.localeCompare(b.from));
    const gaps = [];
    let end = null;
    for (const period of periods) {
        if (end !== null && period.from > addDays(end, 1)) {
            gaps.push({ from: addDays(end, 1), to: addDays(period.from, -1) });
        }
        if (end === null || period.to > end)
            end = period.to;
    }
    return {
        coveredTo: end,
        uncovered: payments.length - periods.length,
        gaps,
    };
}
/**
 * The period proposed for a payment: from the day after the last one covered
 * to the end of that quarter; with nothing covered, the quarter before the
 * payment, the usual rhythm of the funds.
 */
export function suggestedTfrPeriod(coveredTo, paidOn) {
    let from;
    if (coveredTo !== null) {
        from = addDays(coveredTo, 1);
    }
    else {
        const year = Number(paidOn.slice(0, 4));
        const quarter = Math.floor((Number(paidOn.slice(5, 7)) - 1) / 3);
        from = quarter === 0 ? isoDate(year - 1, 10, 1) : isoDate(year, quarter * 3 - 2, 1);
    }
    const year = Number(from.slice(0, 4));
    const last = Math.floor((Number(from.slice(5, 7)) - 1) / 3) * 3 + 3;
    return { from, to: isoDate(year, last, daysInMonth(year, last)) };
}
//# sourceMappingURL=people.js.map